import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { GRNPayment } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

interface PaginatedGRNPaymentResponse {
  data: GRNPayment[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetGRNPayments = () => {
  const [payments, setPayments] = useState<GRNPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchPayments = async (
    grnId: string,
    page = 1,
    limit = 15,
    sortBy = 'paymentDate',
    order = 'desc',
    status?: string,
    fromDate?: Date | null,
    toDate?: Date | null
  ) => {
    setLoading(true);
    try {
      const params: any = {
        grnId: grnId,
        offset: page,
        limit: limit,
        sortBy: sortBy,
        order: order,
      };

      if (status) params.status = status;
      if (fromDate) params.fromDate = fromDate.toISOString().split('T')[0];
      if (toDate) params.toDate = toDate.toISOString().split('T')[0];

      const response =
        await axiosTokenInstance.get<PaginatedGRNPaymentResponse>(
          apiUrls.GET_GRN_PAYMENTS,
          {
            params,
            withCredentials: true,
          }
        );

      const mappedPayments = (response.data.data || []).map((payment: any) => ({
        ...payment,
        grnId: payment.ReceivedGoodId,
        amount: payment.paidAmount,
        receivedBy: payment.receiverName,
        transactionReference: payment.paymentRefNo,
      }));

      setPayments(mappedPayments);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setPayments([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deletePayment = async (id: string, grnId: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_GRN_PAYMENT,
        {
          params: { id },
          withCredentials: true,
        }
      );

      setPayments((prevPayments) =>
        prevPayments.filter((payment) => payment.id !== id)
      );

      if (payments.length === 1) {
        setPayments([]);
      }
      showSnackbar(response.data.message, 'success');
    } catch (error: any) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return {
    loading,
    payments,
    totalItems,
    totalPages,
    currentPage,
    fetchPayments,
    deletePayment,
  };
};
